import { LevelMeter, Select } from '@wundo/ui';
import { audioDeviceOptions } from '@/lib/audioDeviceOptions.ts';
import type { AudioSetup } from '@/lib/useAudioSetup.ts';
import { m } from '@/paraglide/messages.js';
import { PracticeMicrophoneBlocked } from './PracticeMicrophoneBlocked.tsx';

export interface PracticeAudioSetupProps {
  setup: AudioSetup;
}

/**
 * Which microphone, which speaker, and proof that the microphone works.
 *
 * The failure it exists to catch — practising for ten minutes into a laptop
 * lid while a headset sits idle — only shows up afterwards, when the
 * conversation is spent.
 *
 * The bar is the part that matters. Device names come from the driver, and on a
 * docked laptop three of them are equally plausible; saying something and
 * watching the bar move is the only test that answers the question.
 */
export function PracticeAudioSetup({ setup }: PracticeAudioSetupProps) {
  const {
    microphones,
    speakers,
    microphoneId,
    speakerId,
    level,
    block,
    chooseMicrophone,
    chooseSpeaker,
    retry,
  } = setup;

  return (
    <div className='mt-4.5 flex flex-col gap-3.5'>
      {block !== null && <PracticeMicrophoneBlocked block={block} onRetry={retry} />}

      {microphones.length > 0 && (
        <label className='flex flex-col gap-2 text-base font-semibold text-ink'>
          {m.practice_session_audio_microphone()}
          <Select
            value={microphoneId ?? ''}
            options={audioDeviceOptions(microphones, (index) =>
              m.practice_session_audio_device_microphone({ index }),
            )}
            wrapperClassName='w-full max-w-90'
            className='w-full'
            onChange={(event) => chooseMicrophone(event.target.value)}
          />
        </label>
      )}

      {microphones.length > 0 && (
        <div className='max-w-90'>
          <LevelMeter value={level} label={m.practice_session_mic_level()} />
        </div>
      )}

      {speakers.length > 0 && (
        <label className='flex flex-col gap-2 text-base font-semibold text-ink'>
          {m.practice_session_audio_speaker()}
          <Select
            value={speakerId ?? ''}
            options={audioDeviceOptions(speakers, (index) =>
              m.practice_session_audio_device_speaker({ index }),
            )}
            wrapperClassName='w-full max-w-90'
            className='w-full'
            onChange={(event) => chooseSpeaker(event.target.value)}
          />
        </label>
      )}
    </div>
  );
}
